import { DEFAULT_CURRENCY } from "./currencyResolution.js";
import {
  formatCopToCurrencyRate,
  formatFxEffectiveDate,
  formatFxSourceLabel,
  resolveCurrentFxRate,
} from "../utils/automap/buyerFxHistoryFormatters.js";

export const STORAGE_CURRENCY = DEFAULT_CURRENCY;

export function isConvertedDisplayCurrency(displayCurrency, storageCurrency = STORAGE_CURRENCY) {
  return Boolean(displayCurrency && displayCurrency !== storageCurrency);
}

/**
 * Whether listing/detail prices should show the approximate (≈) marker.
 * Opt-in only — converted display currencies no longer default to ≈.
 */
export function shouldShowFxApproximation({
  displayCurrency,
  storageCurrency = STORAGE_CURRENCY,
  ratesAvailable = true,
  showApproximation,
}) {
  if (showApproximation !== true) return false;
  if (!isConvertedDisplayCurrency(displayCurrency, storageCurrency)) return false;
  if (ratesAvailable === false) return false;
  return true;
}

/**
 * Primary FX disclaimer for converted-currency surfaces.
 */
export function buildFxDisclaimerMessage({
  t,
  displayCurrency,
  ratesAvailable = true,
  ratesMeta = null,
}) {
  if (!isConvertedDisplayCurrency(displayCurrency)) {
    return null;
  }
  if (ratesAvailable === false) {
    return t("automotive.fxRatesUnavailable");
  }
  if (ratesMeta?.stale) {
    return t("automotive.fxRatesStale");
  }
  if (ratesMeta?.isFallback || ratesMeta?.source === "fallback") {
    return t("automotive.fxFallbackNotice");
  }
  return t("automotive.fxApproximationDisclaimer");
}

/**
 * Optional inline rate metadata, e.g. "1 COP = 0.022 RUB · Jul 8, 2026".
 */
export function buildFxRateSummaryLine({
  t,
  locale,
  displayCurrency,
  rates = null,
  ratesMeta = null,
  includeSource = false,
}) {
  if (!isConvertedDisplayCurrency(displayCurrency)) {
    return null;
  }

  const rate = resolveCurrentFxRate({ displayCurrency, rates });
  if (rate == null) {
    return null;
  }

  const formattedRate = formatCopToCurrencyRate(rate, displayCurrency);
  const effectiveDate = ratesMeta?.effectiveDate || ratesMeta?.asOf;
  const dateLabel = formatFxEffectiveDate(effectiveDate, locale);

  let line = dateLabel
    ? t("automotive.fxRateSummaryInline", {
        rate: formattedRate,
        currency: displayCurrency,
        date: dateLabel,
      })
    : t("automotive.fxRateSummaryNoDate", {
        rate: formattedRate,
        currency: displayCurrency,
      });

  if (includeSource) {
    const sourceLabel = formatFxSourceLabel(ratesMeta?.source);
    if (sourceLabel) {
      line = `${line} · ${t("automotive.fxHistory.sourceLabel")}: ${sourceLabel}`;
    }
  }

  return line;
}
